// src/vehicles/reports.js — справките на автопарка (Фаза 3).
// Отваря се от бутона „Справки" на екрана Автопарк; period по подразбиране —
// текущата година. Сметките идват наготово от /vehicles/reports.

import { el, clear, $ } from '../shared/dom.js';
import { money, dfmt } from '../shared/format.js';
import { dateInput } from '../shared/dates.js';
import { toast } from '../shared/toast.js';
import { api } from '../shared/api.js';
import { emptyState } from '../shared/ui.js';
import { state } from '../shared/state.js';

const curList = (list) => (list && list.length) ? list.map((t) => money(t.total, t.currency)).join(' + ') : '—';

// onBack: връщане към основния екран на Автопарка (под-изглед, не нова плочка).
export async function reportsView(page, onBack) {
  clear(page);
  if (!state().activeCompany) return window.CRM.needsCompany(page);

  const year = new Date().getFullYear();
  const fromI = dateInput(year + '-01-01');
  const toI = dateInput(year + '-12-31');
  const yearSel = el('select', {}, ...[year, year - 1, year - 2].map((y) =>
    el('option', { value: y, text: 'отчет за ' + y + ' г.', selected: y === year })));

  const head = el('div', { class: 'panel-head' },
    el('h2', { text: 'Автопарк — справки' }),
    el('span', { style: 'color:var(--muted);font-size:13px', text: 'от' }), fromI,
    el('span', { style: 'color:var(--muted);font-size:13px', text: 'до' }), toI,
    yearSel,
    el('button', { class: 'btn sm', text: 'Покажи', onClick: () => load() }),
    el('button', { class: 'btn sm ghost', text: '← Към автопарка', style: 'margin-left:auto', onClick: () => onBack && onBack() }));

  const body = el('div', {});
  page.appendChild(el('div', { class: 'panel' }, head));
  page.appendChild(body);

  async function load() {
    clear(body);
    body.appendChild(el('div', { class: 'cal-hint', text: 'Зареждане…' }));
    let r;
    try {
      const q = new URLSearchParams({
        company_id: state().activeCompany,
        from: fromI.dataset.iso || '', to: toI.dataset.iso || '',
        year: yearSel.value,
      });
      r = await api('/vehicles/reports?' + q.toString());
    } catch (e) { clear(body); toast(e.message, true); return; }
    clear(body);

    // Пълната карта от route-а — не by_vehicle: кола само със стара
    // застраховка няма ред в by_vehicle, но рег. номерът ѝ трябва.
    const regOf = new Map((r.vehicles || []).map((v) => [Number(v.id), v.reg_number]));
    (r.by_vehicle || []).forEach((v) => { if (!regOf.has(Number(v.vehicle_id))) regOf.set(Number(v.vehicle_id), v.reg_number); });

    // ---- По автомобил ----
    const vPanel = el('div', { class: 'panel' }, el('div', { class: 'panel-head' }, el('h2', { text: 'По автомобил' })));
    if (!(r.by_vehicle || []).length) vPanel.appendChild(emptyState('⛟', 'Няма данни за периода.'));
    else {
      const tb = el('tbody');
      r.by_vehicle.forEach((v) => tb.appendChild(el('tr', {},
        el('td', {}, el('strong', { text: v.reg_number || '—' }), el('div', { style: 'font-size:12px;color:var(--muted)', text: v.make_model || '' })),
        el('td', { class: 'num', text: v.km + ' км' }),
        el('td', { class: 'num hide-sm', text: v.fuel_l ? v.fuel_l + ' л' : '—' }),
        el('td', { class: 'num', text: v.avg_l_100 != null ? v.avg_l_100 + ' л/100' : '—' }),
        el('td', { class: 'num', text: curList(v.costs) }),
        el('td', { class: 'num hide-sm', text: curList(v.cost_per_km) })
      )));
      vPanel.appendChild(el('table', {}, el('thead', {}, el('tr', {},
        el('th', { text: 'Кола' }), el('th', { class: 'num', text: 'Пробег' }), el('th', { class: 'num hide-sm', text: 'Заредено' }),
        el('th', { class: 'num', text: 'Среден разход' }), el('th', { class: 'num', text: 'Разходи' }), el('th', { class: 'num hide-sm', text: 'На км' }))), tb));
      vPanel.appendChild(el('div', { class: 'cal-hint', text: 'Средният разход е от зареденото гориво (разходи „Гориво" с литри) спрямо изминатите километри по пътните листа.' }));
    }
    body.appendChild(vPanel);

    // ---- По шофьор ----
    const dPanel = el('div', { class: 'panel' }, el('div', { class: 'panel-head' }, el('h2', { text: 'По шофьор' })));
    if (!(r.by_driver || []).length) dPanel.appendChild(emptyState('☺', 'Няма пътни листа за периода.'));
    else {
      const tb = el('tbody');
      r.by_driver.forEach((d) => tb.appendChild(el('tr', {},
        el('td', {}, el('strong', { text: d.driver })),
        el('td', { class: 'num', text: d.km + ' км' }),
        el('td', { class: 'num', text: d.waybills + ' листа' }),
        el('td', { class: 'num hide-sm', text: d.fuel_norm_l + ' л по норма' })
      )));
      dPanel.appendChild(el('table', {}, el('thead', {}, el('tr', {},
        el('th', { text: 'Шофьор' }), el('th', { class: 'num', text: 'Пробег' }), el('th', { class: 'num', text: 'Пътни листа' }), el('th', { class: 'num hide-sm', text: 'Гориво' }))), tb));
    }
    body.appendChild(dPanel);

    // ---- Месечен отчет ----
    const mPanel = el('div', { class: 'panel' }, el('div', { class: 'panel-head' },
      el('h2', { text: 'Месечен отчет ' + (r.year || '') })));
    const mtb = el('tbody');
    const MONTHS = ['януари', 'февруари', 'март', 'април', 'май', 'юни', 'юли', 'август', 'септември', 'октомври', 'ноември', 'декември'];
    let yKm = 0; const yCosts = {};
    (r.by_month || []).forEach((m, i) => {
      yKm = Math.round((yKm + m.km) * 100) / 100;
      (m.costs || []).forEach((c) => { yCosts[c.currency] = Math.round(((yCosts[c.currency] || 0) + c.total) * 100) / 100; });
      if (!m.km && !(m.costs || []).length) return; // празните месеци не тежат
      mtb.appendChild(el('tr', {},
        el('td', { text: MONTHS[i] }),
        el('td', { class: 'num', text: m.km + ' км' }),
        el('td', { class: 'num', text: curList(m.costs) })));
    });
    mtb.appendChild(el('tr', { style: 'font-weight:700' },
      el('td', { text: 'ОБЩО за годината' }),
      el('td', { class: 'num', text: yKm + ' км' }),
      el('td', { class: 'num', text: curList(Object.keys(yCosts).sort().map((c) => ({ currency: c, total: yCosts[c] }))) })));
    mPanel.appendChild(el('table', {}, el('thead', {}, el('tr', {},
      el('th', { text: 'Месец' }), el('th', { class: 'num', text: 'Пробег' }), el('th', { class: 'num', text: 'Разходи' }))), mtb));
    body.appendChild(mPanel);

    // ---- Ремонти и застраховки ----
    const two = el('div', { class: 'fleet-recent' });
    const repPanel = el('div', { class: 'panel' }, el('div', { class: 'panel-head' }, el('h2', { text: 'Всички ремонти и обслужване' })));
    if (!(r.repairs || []).length) repPanel.appendChild(emptyState('⚙', 'Няма записи за периода.'));
    else {
      const tb = el('tbody');
      r.repairs.forEach((x) => tb.appendChild(el('tr', {},
        el('td', { text: dfmt(x.date) }),
        el('td', {}, el('strong', { text: regOf.get(Number(x.vehicle_id)) || '—' })),
        el('td', { text: x.what }),
        el('td', { class: 'num', text: x.amount ? money(x.amount, x.currency) : '—' })
      )));
      repPanel.appendChild(el('table', {}, el('thead', {}, el('tr', {},
        el('th', { text: 'Дата' }), el('th', { text: 'Кола' }), el('th', { text: 'Какво' }), el('th', { class: 'num', text: 'Сума' }))), tb));
    }
    two.appendChild(repPanel);

    const insPanel = el('div', { class: 'panel' }, el('div', { class: 'panel-head' }, el('h2', { text: 'Всички застраховки' })));
    if (!(r.insurances || []).length) insPanel.appendChild(emptyState('▤', 'Няма застраховки.'));
    else {
      const tb = el('tbody');
      r.insurances.forEach((x) => tb.appendChild(el('tr', {},
        el('td', { text: (x.date ? dfmt(x.date) : '—') + (x.valid_to ? ' – ' + dfmt(x.valid_to) : '') }),
        el('td', {}, el('strong', { text: regOf.get(Number(x.vehicle_id)) || '—' })),
        el('td', {}, x.what, x.unpaid ? el('span', { class: 'tag amber', style: 'margin-left:6px', text: x.unpaid + ' неплатени' }) : null),
        el('td', { class: 'num', text: x.amount ? money(x.amount, x.currency) : '—' })
      )));
      insPanel.appendChild(el('table', {}, el('thead', {}, el('tr', {},
        el('th', { text: 'Период' }), el('th', { text: 'Кола' }), el('th', { text: 'Застраховка' }), el('th', { class: 'num', text: 'Сума' }))), tb));
    }
    two.appendChild(insPanel);
    body.appendChild(two);
  }

  await load();
}
